import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, ListChecks, Hash, MapPin, KeyRound, X, ChevronDown, Search, AtSign, Loader2, UsersRound, Pencil, Check } from 'lucide-react';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { performRuleAction, addOrModifyRule, deleteRule, renameRule } from 'services/accounts/accountService';
import { globalInstagramSearch } from 'services/audience/audienceFilterService';
import AccountDefaultImage from 'assets/images/avatars/default-avatar.jpg';
import { useTheme } from 'contexts/ThemeContext';

interface Account {
  _id: string;
  username: string;
  name: string;
  profile_picture_base64?: string;
}

interface FilterItem {
  type: string;
  filter: string;
  id?: number;
  profile_pic?: string;
}

interface AddedCriteriaItem {
  filter: string;
  id?: number;
  profile_pic?: string;
}

interface Rule {
  _id: string;
  rule_name: string;
  rule: FilterItem[];
  status: string;
  is_active: boolean;
  actions: number;
}

// Categories and their targets for the Add criteria modal
const CATEGORY_OPTIONS = [
  { value: 'users', label: 'Users' },
  { value: 'hashtags', label: 'Hashtags' },
  { value: 'locations', label: 'Locations' },
  { value: 'keywords', label: 'Keywords' },
];

const TARGET_OPTIONS: Record<string, { value: string; label: string; description: string }[]> = {
  users: [
    { value: 'User (followers)', label: 'Followers of accounts', description: 'Target users who follow specific accounts' },
    { value: 'User (followings)', label: 'Followings of accounts', description: 'Target users followed by specific accounts' },
    { value: 'User Posts (likers)', label: 'Likers of posts from accounts', description: 'Target users who liked posts from specific accounts' },
    { value: 'User (specific user)', label: 'Specific users', description: 'Target specific individual users' },
  ],
  hashtags: [
    { value: '#Hashtags', label: 'Posts with hashtags', description: 'Target posts that use specific hashtags' },
    { value: '#Hashtags Posts (likers)', label: 'Likers of posts with hashtags', description: 'Target users who liked posts with specific hashtags' },
  ],
  locations: [
    { value: 'Places', label: 'Posts from locations', description: 'Target posts from specific locations' },
    { value: 'Places Posts (likers)', label: 'Likers of posts from locations', description: 'Target users who liked posts from specific locations' },
  ],
  keywords: [
    { value: 'Posts by Keyword', label: 'Posts containing keywords', description: 'Target posts containing specific keywords' },
    { value: 'Posts by Keyword (likers)', label: 'Likers of posts with keywords', description: 'Target users who liked posts with specific keywords' },
    { value: 'Reels by Keyword', label: 'Reels containing keywords', description: 'Target reels containing specific keywords' },
    { value: 'Reels by Keyword (likers)', label: 'Likers of reels with keywords', description: 'Target users who liked reels with specific keywords' },
  ],
};

type SectionKey = 'users' | 'posts' | 'reels';

const RuleDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const navState = (location.state as { rule?: Rule; selectedAccount?: Account; accounts?: Account[] }) || {};
  const [rule, setRule] = useState<Rule | null>(navState.rule || null);
  const [selectedAccount] = useState<Account | null>(navState.selectedAccount || null);

  const { isDark } = useTheme();

  // Translated type label lookup
  const getTypeLabel = (typeName: string) => t(`rules.detail.typeLabels.${typeName}`, typeName);
  const [activeTab, setActiveTab] = useState<'criteria' | 'settings'>('criteria');
  const [isActivating, setIsActivating] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [isRenameSaving, setIsRenameSaving] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Add criteria modal state
  const [showAddCriteria, setShowAddCriteria] = useState(false);
  const [addCategory, setAddCategory] = useState('');
  const [addTarget, setAddTarget] = useState('');
  const [addFilterValue, setAddFilterValue] = useState('');
  const [addedItems, setAddedItems] = useState<AddedCriteriaItem[]>([]);
  const [isSavingCriteria, setIsSavingCriteria] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isTargetOpen, setIsTargetOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const targetDropdownRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redirect if no rule data
  useEffect(() => {
    if (!rule || !selectedAccount) {
      navigate('/rules', { replace: true });
    }
  }, [rule, selectedAccount, navigate]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (targetDropdownRef.current && !targetDropdownRef.current.contains(event.target as Node)) {
        setIsTargetOpen(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reverse-map a target type name to its category
  const getCategoryForTarget = (targetValue: string): string => {
    for (const [category, targets] of Object.entries(TARGET_OPTIONS)) {
      if (targets.some(t => t.value === targetValue)) return category;
    }
    return '';
  };

  const openAddCriteriaModal = (preCategory?: string, preTarget?: string) => {
    setAddCategory(preCategory || '');
    setAddTarget(preTarget || '');
    setAddFilterValue('');
    setAddedItems([]);
    setSearchResults([]);
    setShowSearchDropdown(false);
    setShowAddCriteria(true);
  };

  // Determine search type from the selected target
  const getSearchType = (target: string): string | null => {
    if (target.toLowerCase().includes('user')) return 'user';
    if (target.toLowerCase().includes('hashtag')) return 'hashtag';
    if (target.toLowerCase().includes('place')) return 'place';
    return null; // keywords = manual entry, no search
  };

  const isSearchableTarget = addTarget ? getSearchType(addTarget) !== null : false;

  // Get context-aware icon for the input field
  const getInputIcon = () => {
    if (addCategory === 'users') return <UsersRound size={20} />;
    if (addCategory === 'hashtags') return <Hash size={20} />;
    if (addCategory === 'locations') return <MapPin size={20} />;
    return <Search size={20} />;
  };

  // Get context-aware input section label
  const getInputLabel = () => {
    if (addCategory === 'users') return 'Accounts';
    if (addCategory === 'hashtags') return 'Hashtags';
    if (addCategory === 'locations') return 'Locations';
    return 'Keywords';
  };

  // Get context-aware placeholder
  const getInputPlaceholder = () => {
    if (addCategory === 'users') return 'Search accounts';
    if (addCategory === 'hashtags') return 'Search hashtags';
    if (addCategory === 'locations') return 'Search locations';
    return 'Enter keyword';
  };

  // Get context-aware action button text
  const getActionButtonText = () => {
    if (addCategory === 'users') return 'Add selected accounts';
    if (addCategory === 'hashtags') return 'Add selected hashtags';
    if (addCategory === 'locations') return 'Add selected locations';
    if (addCategory === 'keywords') return 'Add selected keywords';
    return 'Add selected items';
  };

  // Get selected count text
  const getSelectedCountText = () => {
    const count = addedItems.length;
    if (addCategory === 'users') return `${count} account${count !== 1 ? 's' : ''} selected`;
    if (addCategory === 'hashtags') return `${count} hashtag${count !== 1 ? 's' : ''} selected`;
    if (addCategory === 'locations') return `${count} location${count !== 1 ? 's' : ''} selected`;
    if (addCategory === 'keywords') return `${count} keyword${count !== 1 ? 's' : ''} selected`;
    return `${count} item${count !== 1 ? 's' : ''} selected`;
  };

  // Debounced search for searchable targets
  const handleSearchInput = (value: string) => {
    setAddFilterValue(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    const searchType = getSearchType(addTarget);
    if (!searchType || !value.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!selectedAccount) return;
      setIsSearching(true);
      setShowSearchDropdown(true);
      try {
        const results = await globalInstagramSearch(value.trim(), searchType, selectedAccount._id);
        setSearchResults(Array.isArray(results) ? results : []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);
  };

  // Toggle selection of a search result item (multi-select)
  const handleSelectSearchResult = (item: any) => {
    const searchType = getSearchType(addTarget);
    let criteriaItem: AddedCriteriaItem | null = null;

    if (searchType === 'user') {
      const filterName = item.username || item.full_name || '';
      if (!filterName) return;
      criteriaItem = {
        filter: filterName,
        id: item.pk,
        profile_pic: item.profile_pic_url,
      };
    } else if (searchType === 'hashtag') {
      const filterName = item.name || '';
      if (!filterName) return;
      criteriaItem = { filter: filterName };
    } else if (searchType === 'place') {
      const filterName = item.locationName || item.name || '';
      if (!filterName) return;
      criteriaItem = {
        filter: filterName,
        id: item.pk,
      };
    }

    if (!criteriaItem) return;

    // Toggle: if already added (by filter name), remove; otherwise add
    const exists = addedItems.some(i => i.filter === criteriaItem!.filter);
    if (exists) {
      setAddedItems(prev => prev.filter(i => i.filter !== criteriaItem!.filter));
    } else {
      setAddedItems(prev => [...prev, criteriaItem!]);
    }
  };

  const handleAddItem = () => {
    const val = addFilterValue.trim();
    if (!val || addedItems.some(i => i.filter === val)) return;
    setAddedItems(prev => [...prev, { filter: val }]);
    setAddFilterValue('');
  };

  const handleRemoveAddedItem = (filterName: string) => {
    setAddedItems(prev => prev.filter(i => i.filter !== filterName));
  };

  const handleSaveCriteria = async () => {
    if (!selectedAccount || !rule || !addTarget || addedItems.length === 0) {
      return;
    }
    setIsSavingCriteria(true);
    try {
      const newFilters: FilterItem[] = addedItems.map(item => ({
        type: addTarget,
        filter: item.filter,
        ...(item.id !== undefined && { id: item.id }),
        ...(item.profile_pic && { profile_pic: item.profile_pic }),
      }));
      const updatedRule = [...rule.rule, ...newFilters];
      await addOrModifyRule(selectedAccount._id, rule.rule_name, updatedRule);
      setRule({ ...rule, rule: updatedRule });
      setShowAddCriteria(false);
    } catch (error) {
      console.error('[handleSaveCriteria] error:', error);
    } finally {
      setIsSavingCriteria(false);
    }
  };

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark
    ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
    : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const buttonBg = mainBg;
  const buttonBorder = isDark ? '#314158' : '#E2E8F0';
  const buttonIconColor = isDark ? '#CAD5E2' : '#45556C';
  const overviewCardBg = isDark ? '#0F172B' : '#FFFFFF';
  const overviewCardBorder = isDark ? '#1D293D' : '#F1F5F9';
  const criteriaCardBg = isDark ? '#0F172B' : '#FFFFFF';
  const criteriaCardBorder = isDark ? '#314158' : '#E2E8F0';
  const ctaCardBg = isDark ? '#1D293D' : '#F8FAFC';
  const ctaCardBorder = isDark ? '#314158' : '#E2E8F0';
  const tabActiveColor = isDark ? '#FFFFFF' : '#314158';
  const tabInactiveColor = '#90A1B9';
  const tabSelectorColor = isDark ? '#E2E8F0' : '#314158';
  const switchBg = isDark ? '#45556C' : '#E2E8F0';
  const switchActiveBg = isDark ? '#2B7FFF' : '#2B7FFF';
  const switchKnobColor = isDark ? '#0F172B' : '#FFFFFF';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#62748E';
  const deleteButtonBorder = isDark ? '#EC003F' : '#FFA1AD';
  const deleteButtonColor = isDark ? '#FF637E' : '#FF2056';

  if (!rule || !selectedAccount) return null;

  const normalizedStatus = rule.status?.toLowerCase().replace(/\s+/g, '_') || 'active';
  const criteria = rule.rule || [];
  const criteriaCount = criteria.length;
  const hashtagsCount = criteria.filter(c => c.type.includes('#Hashtags')).length;
  const locationsCount = criteria.filter(c => c.type.includes('Places')).length;
  const keywordsCount = criteria.filter(c => c.type.includes('Keyword')).length;

  // Status tag
  const getStatusTag = (status: string) => {
    const norm = status?.toLowerCase().replace(/\s+/g, '_') || 'active';
    const styles: Record<string, { bg: string; border: string; color: string; label: string }> = {
      active: {
        bg: isDark ? '#022C22' : '#ECFDF5',
        border: isDark ? '#009966' : '#5EE9B5',
        color: isDark ? '#5EE9B5' : '#009966',
        label: t('rules.status.active', 'Active'),
      },
      paused: {
        bg: isDark ? '#461901' : '#FFFBEB',
        border: isDark ? '#E17100' : '#FFD230',
        color: isDark ? '#FFD230' : '#E17100',
        label: t('rules.status.paused', 'Paused'),
      },
      ready_to_activate: {
        bg: isDark ? '#162456' : '#EFF6FF',
        border: isDark ? '#155DFC' : '#8EC5FF',
        color: isDark ? '#8EC5FF' : '#155DFC',
        label: t('rules.status.readyToActivate', 'Ready to activate'),
      },
    };
    const s = styles[norm] || styles.active;
    return (
      <span
        style={{
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 500,
          background: s.bg,
          border: `1px solid ${s.border}`,
          color: s.color,
          borderRadius: '999px',
          padding: '2px 8px',
          whiteSpace: 'nowrap' as const,
        }}
      >
        {s.label}
      </span>
    );
  };

  // Tag color styles by criteria type
  const getTagStyle = (type: string) => {
    if (type.startsWith('User')) {
      return {
        bg: isDark ? '#162456' : '#EFF6FF',
        color: isDark ? '#8EC5FF' : '#2B7FFF',
        xColor: isDark ? '#2B7FFF' : '#155DFC',
      };
    }
    if (type.includes('#Hashtags')) {
      return {
        bg: isDark ? '#3C0366' : '#FAF5FF',
        color: isDark ? '#DAB2FF' : '#AD46FF',
        xColor: isDark ? '#AD46FF' : '#9810FA',
      };
    }
    if (type.includes('Keyword')) {
      return {
        bg: isDark ? '#053345' : '#ECFFFE',
        color: isDark ? '#53EAFD' : '#00B8DB',
        xColor: isDark ? '#00B8DB' : '#0891B2',
      };
    }
    if (type.includes('Places')) {
      return {
        bg: isDark ? '#022C22' : '#ECFDF5',
        color: isDark ? '#5EE9B5' : '#009966',
        xColor: isDark ? '#00BC7D' : '#047857',
      };
    }
    return {
      bg: isDark ? '#162456' : '#EFF6FF',
      color: isDark ? '#8EC5FF' : '#2B7FFF',
      xColor: isDark ? '#2B7FFF' : '#155DFC',
    };
  };

  // Group criteria into sections
  const groupCriteria = () => {
    const sections: Record<SectionKey, Record<string, FilterItem[]>> = {
      users: {},
      posts: {},
      reels: {},
    };

    criteria.forEach(item => {
      let section: SectionKey;
      if (item.type.includes('Reels')) {
        section = 'reels';
      } else if (item.type.startsWith('User')) {
        section = 'users';
      } else {
        section = 'posts';
      }
      if (!sections[section][item.type]) {
        sections[section][item.type] = [];
      }
      sections[section][item.type].push(item);
    });

    return sections;
  };

  const groupedSections = groupCriteria();

  // Handlers
  const handleActivate = async () => {
    if (!selectedAccount || !rule) return;
    setIsActivating(true);
    try {
      await performRuleAction(selectedAccount._id, rule.rule_name, 'Active', true);
      setRule({ ...rule, status: 'Active', is_active: true });
    } catch {
      // Error toast handled by service
    } finally {
      setIsActivating(false);
    }
  };

  const handleRemoveFilter = async (filterToRemove: FilterItem) => {
    if (!selectedAccount || !rule) return;
    const updatedFilters = rule.rule.filter(f =>
      !(f.type === filterToRemove.type && f.filter === filterToRemove.filter)
    );
    try {
      await addOrModifyRule(selectedAccount._id, rule.rule_name, updatedFilters);
      setRule({ ...rule, rule: updatedFilters });
    } catch {
      // Error toast handled by service
    }
  };

  const handleToggleActive = async () => {
    if (!selectedAccount || !rule || isTogglingActive) return;
    setIsTogglingActive(true);
    try {
      const isCurrentlyActive = rule.status?.toLowerCase() === 'active';
      const newIsActive = !isCurrentlyActive;
      const newStatus = newIsActive ? 'Active' : 'Paused';
      await performRuleAction(selectedAccount._id, rule.rule_name, newStatus, newIsActive);
      setRule({ ...rule, status: newStatus, is_active: newIsActive });
    } catch {
      // Error toast handled by service
    } finally {
      setIsTogglingActive(false);
    }
  };

  const startRename = () => {
    if (!rule) return;
    setRenameValue(rule.rule_name);
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.focus(), 50);
  };

  const handleRename = async () => {
    if (!rule || isRenameSaving) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === rule.rule_name) {
      setIsRenaming(false);
      return;
    }
    setIsRenameSaving(true);
    try {
      await renameRule(rule._id, trimmed);
      setRule({ ...rule, rule_name: trimmed });
      setIsRenaming(false);
    } catch {
      // Error toast handled by service
    } finally {
      setIsRenameSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!rule || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteRule(rule._id);
      navigate('/rules');
    } catch {
      // Error toast handled by service
    } finally {
      setIsDeleting(false);
    }
  };

  // Overview cards config
  const overviewCards = [
    {
      icon: <ListChecks size={24} />,
      iconBg: isDark ? '#162456' : '#EFF6FF',
      iconColor: isDark ? '#51A2FF' : '#2B7FFF',
      count: criteriaCount,
      label: t('rules.detail.overview.criteria', 'Criteria'),
    },
    {
      icon: <Hash size={24} />,
      iconBg: isDark ? '#3C0366' : '#FAF5FF',
      iconColor: isDark ? '#C27AFF' : '#AD46FF',
      count: hashtagsCount,
      label: t('rules.detail.overview.hashtags', 'Hashtags'),
    },
    {
      icon: <MapPin size={24} />,
      iconBg: isDark ? '#022C22' : '#ECFDF5',
      iconColor: isDark ? '#00D492' : '#00BC7D',
      count: locationsCount,
      label: t('rules.detail.overview.locations', 'Locations'),
    },
    {
      icon: <KeyRound size={24} />,
      iconBg: isDark ? '#053345' : '#ECFFFE',
      iconColor: isDark ? '#00D3F2' : '#00B8DB',
      count: keywordsCount,
      label: t('rules.detail.overview.keywords', 'Keywords'),
    },
  ];

  const sectionLabels: Record<SectionKey, string> = {
    users: t('rules.detail.sections.users', 'Users'),
    posts: t('rules.detail.sections.posts', 'Posts'),
    reels: t('rules.detail.sections.reels', 'Reels'),
  };

  return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        gap: '8px',
        background: containerBg,
      }}
    >
      <div
        className="flex flex-col flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{
          background: mainBg,
          boxShadow: mainShadow,
        }}
      >
        {/* Header */}
        <div
          className="flex flex-col flex-shrink-0"
          style={{
            padding: '12px 16px 0px',
            borderBottom: `1px solid ${headerBorder}`,
            background: mainBg,
            gap: '8px',
          }}
        >
          {/* Row 1: Back + Name */}
          <div className="flex items-center" style={{ gap: '8px' }}>
            <button
              className="flex items-center flex-shrink-0 transition-opacity hover:opacity-80"
              style={{
                padding: '8px 12px',
                background: buttonBg,
                border: `1px solid ${buttonBorder}`,
                borderRadius: '8px',
                cursor: 'pointer',
                height: '36px',
                gap: '4px',
              }}
              onClick={() => navigate('/rules')}
            >
              <ArrowLeft size={20} color={buttonIconColor} />
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}>
                {t('rules.detail.back', 'Back')}
              </span>
            </button>

            {isRenaming ? (
              <div className="flex items-center gap-1.5" style={{ flex: 1, minWidth: 0 }}>
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setIsRenaming(false); }}
                  className="truncate"
                  style={{
                    fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor,
                    margin: 0, flex: 1, minWidth: 0, background: 'transparent',
                    border: `1px solid ${isDark ? '#314158' : '#D1D5DB'}`, borderRadius: '6px',
                    padding: '2px 8px', outline: 'none',
                  }}
                />
                <button
                  onClick={handleRename}
                  disabled={isRenameSaving}
                  className="flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-80"
                  style={{ width: '28px', height: '28px', borderRadius: '6px', background: isDark ? '#0D2818' : '#DCFCE7', border: 'none', cursor: 'pointer', opacity: isRenameSaving ? 0.5 : 1 }}
                >
                  {isRenameSaving ? <Loader2 size={14} className="animate-spin" style={{ color: isDark ? '#5EE9B5' : '#16A34A' }} /> : <Check size={14} style={{ color: isDark ? '#5EE9B5' : '#16A34A' }} />}
                </button>
                <button
                  onClick={() => setIsRenaming(false)}
                  className="flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-80"
                  style={{ width: '28px', height: '28px', borderRadius: '6px', background: isDark ? '#2A1215' : '#FEE2E2', border: 'none', cursor: 'pointer' }}
                >
                  <X size={14} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5" style={{ flex: 1, minWidth: 0 }}>
                <h1 className="truncate" style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0, minWidth: 0 }}>
                  {rule.rule_name}
                </h1>
                <button
                  onClick={startRename}
                  className="flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-70"
                  style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  title={t('rules.detail.rename', 'Rename rule')}
                >
                  <Pencil size={13} style={{ color: subtitleColor }} />
                </button>
              </div>
            )}
          </div>

          {/* Row 2: Status tag + Add criteria */}
          <div className="flex items-center justify-between" style={{ gap: '8px' }}>
            {getStatusTag(rule.status)}

            <button
              className="flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-90"
              style={{
                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                borderRadius: '8px',
                padding: '8px 12px',
                gap: '4px',
                border: 'none',
                cursor: 'pointer',
                height: '36px',
              }}
              onClick={() => openAddCriteriaModal()}
            >
              <Plus size={20} color="#FFFFFF" />
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: '#FFFFFF' }}>
                {t('rules.detail.addCriteria', 'Add criteria')}
              </span>
            </button>
          </div>

          {/* Row 2: Tabs */}
          <div className="flex" style={{ gap: '8px' }}>
            <button
              className="relative flex items-center justify-center"
              style={{
                padding: '8px 4px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setActiveTab('criteria')}
            >
              <span
                style={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: 500,
                  color: activeTab === 'criteria' ? tabActiveColor : tabInactiveColor,
                  padding: '2px 4px',
                }}
              >
                {t('rules.detail.tabs.criteria', 'Criteria')}
              </span>
              {activeTab === 'criteria' && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '8px',
                    right: '8px',
                    height: '2px',
                    background: tabSelectorColor,
                  }}
                />
              )}
            </button>
            <button
              className="relative flex items-center justify-center"
              style={{
                padding: '8px 4px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setActiveTab('settings')}
            >
              <span
                style={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: 500,
                  color: activeTab === 'settings' ? tabActiveColor : tabInactiveColor,
                  padding: '2px 4px',
                }}
              >
                {t('rules.detail.tabs.settings', 'Settings')}
              </span>
              {activeTab === 'settings' && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '8px',
                    right: '8px',
                    height: '2px',
                    background: tabSelectorColor,
                  }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className={`flex-1 min-h-0 overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
          style={{ padding: '16px' }}
        >
          {activeTab === 'criteria' && (
          <div className="flex flex-col" style={{ gap: '24px' }}>
            {/* CTA Card - only when ready_to_activate */}
            {normalizedStatus === 'ready_to_activate' && (
              <div
                className="flex flex-col items-center"
                style={{
                  background: ctaCardBg,
                  border: `1px solid ${ctaCardBorder}`,
                  borderRadius: '8px',
                  padding: '24px',
                  gap: '24px',
                }}
              >
                <Logomark style={{ width: '80px', height: '80px' }} />
                <div className="flex flex-col items-center" style={{ gap: '8px' }}>
                  <span style={{ fontSize: '24px', lineHeight: '32px', fontWeight: 700, color: titleColor, textAlign: 'center' }}>
                    {t('rules.detail.cta.title', 'Ready to activate this automation?')}
                  </span>
                  <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, color: titleColor, textAlign: 'center', whiteSpace: 'pre-line' }}>
                    {t('rules.detail.cta.description', 'Your targeting rules are set and ready.\nOnce activated, this automation will start reaching new users based on your criteria.')}
                  </span>
                </div>
                <button
                  className="flex items-center justify-center transition-opacity hover:opacity-90"
                  style={{
                    background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                    borderRadius: '12px',
                    padding: '8px 16px',
                    border: 'none',
                    cursor: isActivating ? 'default' : 'pointer',
                    opacity: isActivating ? 0.5 : 1,
                  }}
                  onClick={handleActivate}
                  disabled={isActivating}
                >
                  <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: '#FFFFFF' }}>
                    {t('rules.detail.cta.activate', 'Activate automation')}
                  </span>
                </button>
              </div>
            )}

            {/* Overview Section */}
            <div className="flex flex-col" style={{ gap: '16px' }}>
              <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>
                {t('rules.detail.overview.title', 'Overview')}
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: '8px' }}>
                {overviewCards.map((card, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col"
                    style={{
                      background: overviewCardBg,
                      border: `1px solid ${overviewCardBorder}`,
                      borderRadius: '16px',
                      padding: '16px',
                      gap: '8px',
                    }}
                  >
                    <div
                      className="flex items-center justify-center"
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '12px',
                        background: card.iconBg,
                        color: card.iconColor,
                      }}
                    >
                      {card.icon}
                    </div>
                    <div className="flex flex-col" style={{ gap: '2px' }}>
                      <span style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor }}>
                        {card.count}
                      </span>
                      <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                        {card.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Criteria Sections */}
            {(['users', 'posts', 'reels'] as SectionKey[]).map(sectionKey => {
              const types = groupedSections[sectionKey];
              if (Object.keys(types).length === 0) return null;
              return (
                <div key={sectionKey} className="flex flex-col" style={{ gap: '16px' }}>
                  <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}>
                    {sectionLabels[sectionKey]}
                  </span>
                  <div className="flex flex-col" style={{ gap: '8px' }}>
                    {Object.entries(types).map(([typeName, items]) => {
                      const tagStyle = getTagStyle(typeName);
                      const label = getTypeLabel(typeName);
                      const isUserType = typeName.startsWith('User');
                      return (
                        <div
                          key={typeName}
                          className="flex flex-col"
                          style={{
                            background: criteriaCardBg,
                            border: `1px solid ${criteriaCardBorder}`,
                            borderRadius: '8px',
                            padding: '16px',
                            gap: '8px',
                          }}
                        >
                          {/* Label row */}
                          <div className="flex items-center" style={{ gap: '4px' }}>
                            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, color: subtitleColor }}>
                              {t('rules.detail.target', 'Target')}
                            </span>
                            <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: titleColor }}>
                              {label}
                            </span>
                          </div>
                          {/* Tags row */}
                          <div className="flex flex-wrap" style={{ gap: '8px' }}>
                            {items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center"
                                style={{
                                  background: tagStyle.bg,
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  gap: '4px',
                                }}
                              >
                                {isUserType && (
                                  <img
                                    src={item.profile_pic || AccountDefaultImage}
                                    alt={item.filter}
                                    style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover' }}
                                  />
                                )}
                                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: tagStyle.color }}>
                                  {item.filter}
                                </span>
                                <button
                                  className="flex items-center justify-center transition-opacity hover:opacity-70"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                  onClick={() => handleRemoveFilter(item)}
                                >
                                  <X size={20} color={tagStyle.xColor} />
                                </button>
                              </div>
                            ))}
                            <button
                                className="flex items-center justify-center transition-opacity hover:opacity-80"
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  background: buttonBg,
                                  border: `1px solid ${criteriaCardBorder}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => openAddCriteriaModal(getCategoryForTarget(typeName), typeName)}
                              >
                                <Plus size={20} color={titleColor} />
                              </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {activeTab === 'settings' && (
          <div>
            {/* Section: General Settings */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '24px',
                color: labelColor,
                margin: '0 0 12px 0',
              }}>
                {t('rules.detail.settings.generalSettings', 'General Settings')}
              </h2>

              {/* Divider */}
              <div style={{ height: '1px', background: isDark ? '#1D293D' : '#F1F5F9', marginBottom: '20px' }} />

              {/* Activate row */}
              <div
                className="flex items-center justify-between"
                style={{ marginBottom: '20px' }}
              >
                <div>
                  <p style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: labelColor,
                    margin: 0,
                  }}>
                    {t('rules.detail.settings.activateLabel', 'Activate')}
                  </p>
                  <p style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: descriptionColor,
                    margin: 0,
                  }}>
                    {t('rules.detail.settings.activateDescription', 'Enable or disable this automation rule')}
                  </p>
                </div>

                {/* Switch */}
                <button
                  onClick={handleToggleActive}
                  disabled={isTogglingActive}
                  style={{
                    width: '40px',
                    height: '20px',
                    borderRadius: '999px',
                    background: rule.status?.toLowerCase() === 'active' ? switchActiveBg : switchBg,
                    border: 'none',
                    cursor: isTogglingActive ? 'default' : 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: rule.status?.toLowerCase() === 'active' ? 'flex-end' : 'flex-start',
                    transition: 'background 0.2s, justify-content 0.2s',
                    opacity: isTogglingActive ? 0.5 : 1,
                    flexShrink: 0,
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '999px',
                    background: switchKnobColor,
                    transition: 'transform 0.2s',
                  }} />
                </button>
              </div>

              {/* Delete rule row */}
              <div className="flex items-center justify-between">
                <div>
                  <p style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: labelColor,
                    margin: 0,
                  }}>
                    {t('rules.detail.settings.deleteLabel', 'Delete rule')}
                  </p>
                  <p style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: descriptionColor,
                    margin: 0,
                  }}>
                    {t('rules.detail.settings.deleteDescription', 'Permanently remove this automation rule')}
                  </p>
                </div>

                {/* Delete Button */}
                <button
                  className="flex items-center justify-center transition-opacity hover:opacity-80"
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    border: `1px solid ${deleteButtonBorder}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: deleteButtonColor,
                  }}>
                    {t('rules.detail.settings.deleteButton', 'Delete rule')}
                  </span>
                </button>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            style={{ background: 'rgba(0, 0, 0, 0.5)' }}
            onClick={() => setShowDeleteConfirm(false)}
          >
            <div
              className="flex flex-col"
              style={{
                background: mainBg,
                borderRadius: '16px',
                padding: '24px',
                gap: '16px',
                maxWidth: '400px',
                width: '90%',
                border: `1px solid ${buttonBorder}`,
              }}
              onClick={e => e.stopPropagation()}
            >
              <span style={{
                fontFamily: 'Geist, sans-serif',
                fontSize: '18px',
                lineHeight: '28px',
                fontWeight: 700,
                color: titleColor,
              }}>
                {t('rules.detail.settings.deleteConfirmTitle', 'Delete rule?')}
              </span>
              <span style={{
                fontFamily: 'Geist, sans-serif',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 400,
                color: subtitleColor,
              }}>
                {t('rules.detail.settings.deleteConfirmMessage', 'Are you sure you want to delete this rule? This action cannot be undone.')}
              </span>
              <div className="flex justify-end" style={{ gap: '8px' }}>
                <button
                  className="flex items-center justify-center transition-opacity hover:opacity-80"
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    padding: '8px 16px',
                    background: mainBg,
                    border: `1px solid ${buttonBorder}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{
                    fontFamily: 'Geist, sans-serif',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 500,
                    color: titleColor,
                  }}>
                    {t('rules.detail.settings.cancelButton', 'Cancel')}
                  </span>
                </button>
                <button
                  className="flex items-center justify-center transition-opacity hover:opacity-80"
                  onClick={handleDeleteRule}
                  disabled={isDeleting}
                  style={{
                    padding: '8px 16px',
                    background: '#EC003F',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isDeleting ? 'default' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  <span style={{
                    fontFamily: 'Geist, sans-serif',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 500,
                    color: '#FFFFFF',
                  }}>
                    {t('rules.detail.settings.deleteConfirmButton', 'Delete')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Criteria Modal — portaled to body so it covers sidebar */}
        {showAddCriteria && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{
              zIndex: 9999,
              background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddCriteria(false);
              }
            }}
          >
            <div
              className={`flex flex-col scrollbar-hide`}
              style={{
                width: '440px',
                maxWidth: '95vw',
                maxHeight: '90vh',
                borderRadius: '16px',
                background: mainBg,
                overflowY: 'auto',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                className="flex items-start"
                style={{
                  padding: '24px',
                  gap: '8px',
                  borderBottom: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
                  flexShrink: 0,
                  position: 'sticky',
                  top: 0,
                  background: mainBg,
                  zIndex: 10,
                  borderRadius: '16px 16px 0 0',
                }}
              >
                <span style={{
                  fontFamily: 'Geist, sans-serif',
                  fontSize: '20px',
                  lineHeight: '28px',
                  fontWeight: 700,
                  color: titleColor,
                  flex: 1,
                }}>
                  {t('rules.detail.addCriteriaModal.title', 'Add criteria')}
                </span>
                <button
                  className="flex items-center justify-center transition-opacity hover:opacity-70"
                  style={{
                    width: '28px',
                    height: '28px',
                    background: 'rgba(255, 255, 255, 0.01)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    padding: '6px',
                    flexShrink: 0,
                  }}
                  onClick={() => setShowAddCriteria(false)}
                >
                  <X size={16} color={titleColor} />
                </button>
              </div>

              {/* Modal Content */}
              <div
                className="flex flex-col"
                style={{
                  padding: '24px',
                  gap: '24px',
                }}
              >
                {/* Container: Category + Target + Search */}
                <div className="flex flex-col" style={{ gap: '16px' }}>
                  {/* Category Dropdown */}
                  <div className="flex flex-col" style={{ gap: '4px' }}>
                    <span style={{
                      fontFamily: 'Geist, sans-serif',
                      fontSize: '14px',
                      lineHeight: '20px',
                      fontWeight: 500,
                      color: titleColor,
                    }}>
                      {t('rules.detail.addCriteriaModal.category', 'Category')}
                    </span>
                    <div ref={categoryDropdownRef}>
                      <button
                        className="flex items-center justify-between w-full"
                        style={{
                          padding: '8px 12px',
                          height: '36px',
                          background: mainBg,
                          border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                          borderRadius: isCategoryOpen ? '8px 8px 0 0' : '8px',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                        }}
                        onClick={() => { setIsCategoryOpen(!isCategoryOpen); setIsTargetOpen(false); }}
                      >
                        <span style={{
                          fontFamily: 'Geist, sans-serif',
                          fontSize: '14px',
                          lineHeight: '20px',
                          fontWeight: 400,
                          color: addCategory
                            ? titleColor
                            : (isDark ? '#62748E' : '#90A1B9'),
                        }}>
                          {addCategory
                            ? CATEGORY_OPTIONS.find(c => c.value === addCategory)?.label
                            : t('rules.detail.addCriteriaModal.selectCategory', 'Select a category')}
                        </span>
                        <ChevronDown
                          size={20}
                          color={isDark ? '#62748E' : '#90A1B9'}
                          style={{ flexShrink: 0 }}
                          className={`transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isCategoryOpen && (
                        <div
                          style={{
                            background: mainBg,
                            border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                            borderTop: 'none',
                            borderRadius: '0 0 8px 8px',
                            overflow: 'hidden',
                          }}
                        >
                          {CATEGORY_OPTIONS.map(opt => (
                            <div
                              key={opt.value}
                              className="cursor-pointer transition-all"
                              style={{
                                padding: '8px 12px',
                                background: addCategory === opt.value
                                  ? (isDark ? '#162456' : '#EFF6FF')
                                  : 'transparent',
                              }}
                              onMouseEnter={(e) => {
                                if (addCategory !== opt.value)
                                  e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                              }}
                              onMouseLeave={(e) => {
                                if (addCategory !== opt.value)
                                  e.currentTarget.style.background = 'transparent';
                              }}
                              onClick={() => {
                                setAddCategory(opt.value);
                                setAddTarget('');
                                setAddedItems([]);
                                setSearchResults([]);
                                setShowSearchDropdown(false);
                                setAddFilterValue('');
                                setIsCategoryOpen(false);
                              }}
                            >
                              <span style={{
                                fontFamily: 'Geist, sans-serif',
                                fontSize: '14px',
                                lineHeight: '20px',
                                fontWeight: 400,
                                color: titleColor,
                              }}>
                                {opt.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Target Dropdown — only shown when category is selected */}
                  {addCategory && (
                    <div className="flex flex-col" style={{ gap: '4px' }}>
                      <span style={{
                        fontFamily: 'Geist, sans-serif',
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: titleColor,
                      }}>
                        {t('rules.detail.addCriteriaModal.target', 'Target')}
                      </span>
                      <div ref={targetDropdownRef}>
                        <button
                          className="flex items-center justify-between w-full"
                          style={{
                            padding: '8px 12px',
                            height: '36px',
                            background: mainBg,
                            border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                            borderRadius: isTargetOpen ? '8px 8px 0 0' : '8px',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                          }}
                          onClick={() => { setIsTargetOpen(!isTargetOpen); setIsCategoryOpen(false); }}
                        >
                          <span style={{
                            fontFamily: 'Geist, sans-serif',
                            fontSize: '14px',
                            lineHeight: '20px',
                            fontWeight: 400,
                            color: addTarget
                              ? titleColor
                              : (isDark ? '#62748E' : '#90A1B9'),
                            textAlign: 'left',
                          }}>
                            {addTarget
                              ? getTypeLabel(addTarget)
                              : t('rules.detail.addCriteriaModal.selectTarget', 'Select a target')}
                          </span>
                          <ChevronDown
                            size={20}
                            color={isDark ? '#62748E' : '#90A1B9'}
                            style={{ flexShrink: 0 }}
                            className={`transition-transform ${isTargetOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isTargetOpen && (
                          <div
                            style={{
                              background: mainBg,
                              border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                              borderTop: 'none',
                              borderRadius: '0 0 8px 8px',
                              overflow: 'hidden',
                            }}
                          >
                            {(TARGET_OPTIONS[addCategory] || []).map(opt => (
                              <div
                                key={opt.value}
                                className="cursor-pointer transition-all"
                                style={{
                                  padding: '8px 12px',
                                  background: addTarget === opt.value
                                    ? (isDark ? '#162456' : '#EFF6FF')
                                    : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                  if (addTarget !== opt.value)
                                    e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                                }}
                                onMouseLeave={(e) => {
                                  if (addTarget !== opt.value)
                                    e.currentTarget.style.background = 'transparent';
                                }}
                                onClick={() => {
                                  setAddTarget(opt.value);
                                  setAddedItems([]);
                                  setSearchResults([]);
                                  setShowSearchDropdown(false);
                                  setAddFilterValue('');
                                  setIsTargetOpen(false);
                                }}
                              >
                                <span style={{
                                  fontFamily: 'Geist, sans-serif',
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  fontWeight: 400,
                                  color: titleColor,
                                }}>
                                  {getTypeLabel(opt.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Search/Input field — only shown when target is selected */}
                  {addTarget && (
                    <div className="flex flex-col" style={{ gap: '4px' }}>
                      <span style={{
                        fontFamily: 'Geist, sans-serif',
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: titleColor,
                      }}>
                        {getInputLabel()}
                      </span>

                      {/* For searchable targets: unified search container with border-radius: 16px */}
                      {isSearchableTarget ? (
                        <div
                          ref={searchDropdownRef}
                          style={{
                            border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                            borderRadius: '16px',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Input row */}
                          <div
                            className="flex items-center"
                            style={{
                              padding: '8px',
                              gap: '4px',
                              background: mainBg,
                              borderBottom: (showSearchDropdown && (isSearching || searchResults.length > 0 || addFilterValue.trim()))
                                ? 'none'
                                : 'none',
                            }}
                          >
                            <div
                              className="flex items-center flex-1"
                              style={{
                                padding: '8px 12px',
                                gap: '4px',
                                background: mainBg,
                                borderRadius: '8px',
                              }}
                            >
                              <span style={{ color: isDark ? '#62748E' : '#90A1B9', display: 'flex', flexShrink: 0 }}>
                                {getInputIcon()}
                              </span>
                              <input
                                type="text"
                                value={addFilterValue}
                                onChange={e => handleSearchInput(e.target.value)}
                                onFocus={() => {
                                  if (searchResults.length > 0) setShowSearchDropdown(true);
                                }}
                                placeholder={getInputPlaceholder()}
                                className="flex-1 outline-none"
                                style={{
                                  fontFamily: 'Geist, sans-serif',
                                  background: 'transparent',
                                  border: 'none',
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: addFilterValue ? titleColor : (isDark ? '#62748E' : '#90A1B9'),
                                  padding: 0,
                                  width: '100%',
                                }}
                              />
                            </div>
                            {/* Search button: 36x36 square, border-radius: 8px */}
                            <button
                              className="flex items-center justify-center transition-opacity hover:opacity-90"
                              style={{
                                width: '36px',
                                height: '36px',
                                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: isSearching ? 'default' : 'pointer',
                                flexShrink: 0,
                                opacity: (!addFilterValue.trim() && !isSearching) ? 0.5 : 1,
                              }}
                              onClick={() => {
                                if (addFilterValue.trim()) handleSearchInput(addFilterValue);
                              }}
                              disabled={isSearching}
                            >
                              {isSearching ? (
                                <Loader2 size={20} color="#FFFFFF" className="animate-spin" />
                              ) : (
                                <Search size={20} color="#FFFFFF" />
                              )}
                            </button>
                          </div>

                          {/* Search results list */}
                          {showSearchDropdown && (isSearching || searchResults.length > 0 || (!isSearching && searchResults.length === 0 && addFilterValue.trim())) && (
                            <div
                              className="flex flex-col"
                              style={{
                                padding: '8px',
                                gap: '2px',
                                background: mainBg,
                                borderRadius: '0 0 16px 16px',
                                maxHeight: '220px',
                                overflowY: 'auto',
                              }}
                            >
                              {isSearching && searchResults.length === 0 && (
                                <div className="flex items-center justify-center" style={{ padding: '16px' }}>
                                  <Loader2
                                    size={20}
                                    color={isDark ? '#51A2FF' : '#2B7FFF'}
                                    className="animate-spin"
                                  />
                                  <span style={{
                                    fontFamily: 'Geist, sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: subtitleColor,
                                    marginLeft: '8px',
                                  }}>
                                    {t('rules.detail.addCriteriaModal.searching', 'Searching...')}
                                  </span>
                                </div>
                              )}
                              {!isSearching && searchResults.length === 0 && addFilterValue.trim() && (
                                <div className="flex items-center justify-center" style={{ padding: '16px' }}>
                                  <span style={{
                                    fontFamily: 'Geist, sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: subtitleColor,
                                  }}>
                                    {t('rules.detail.addCriteriaModal.noResults', 'No results found')}
                                  </span>
                                </div>
                              )}
                              {searchResults.map((item, idx) => {
                                const searchType = getSearchType(addTarget);
                                const displayName = searchType === 'user'
                                  ? (item.username || '')
                                  : searchType === 'hashtag'
                                    ? (item.name || '')
                                    : (item.locationName || item.name || '');
                                const isSelected = addedItems.some(i => i.filter === displayName);

                                return (
                                  <div
                                    key={idx}
                                    className="flex items-center cursor-pointer"
                                    style={{
                                      padding: '12px',
                                      gap: '8px',
                                      height: '64px',
                                      background: isSelected
                                        ? (isDark ? '#162456' : '#EFF6FF')
                                        : 'transparent',
                                      border: isSelected
                                        ? `1px solid ${isDark ? '#155DFC' : '#8EC5FF'}`
                                        : '1px solid transparent',
                                      borderRadius: '8px',
                                      boxSizing: 'border-box',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.background = 'transparent';
                                      } else {
                                        e.currentTarget.style.background = isDark ? '#162456' : '#EFF6FF';
                                      }
                                    }}
                                    onClick={() => handleSelectSearchResult(item)}
                                  >
                                    {/* User avatar */}
                                    {searchType === 'user' && (
                                      <img
                                        src={item.profile_pic_url || AccountDefaultImage}
                                        alt={item.username}
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '12px',
                                          objectFit: 'cover',
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                    {/* Hashtag icon */}
                                    {searchType === 'hashtag' && (
                                      <div
                                        className="flex items-center justify-center"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '12px',
                                          background: isDark ? '#3C0366' : '#FAF5FF',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <Hash size={16} color={isDark ? '#DAB2FF' : '#AD46FF'} />
                                      </div>
                                    )}
                                    {/* Location icon */}
                                    {searchType === 'place' && (
                                      <div
                                        className="flex items-center justify-center"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '12px',
                                          background: isDark ? '#022C22' : '#ECFDF5',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <MapPin size={16} color={isDark ? '#5EE9B5' : '#009966'} />
                                      </div>
                                    )}
                                    {/* Text info */}
                                    <div className="flex flex-col flex-1" style={{ minWidth: 0 }}>
                                      <span style={{
                                        fontFamily: 'Geist, sans-serif',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        fontWeight: 500,
                                        color: isDark ? '#51A2FF' : '#2B7FFF',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}>
                                        {searchType === 'user' ? `@${item.username}`
                                          : searchType === 'hashtag' ? `#${item.name}`
                                          : (item.locationName || item.name)}
                                      </span>
                                      <span style={{
                                        fontFamily: 'Geist, sans-serif',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        fontWeight: 400,
                                        color: isDark ? '#90A1B9' : '#62748E',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}>
                                        {searchType === 'user' ? (item.full_name || '')
                                          : searchType === 'hashtag' ? (item.search_result_subtitle || '')
                                          : (item.subtitle || '')}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* For non-searchable targets (keywords): simple input with inline add button */
                        <div ref={searchDropdownRef}>
                          <div
                            className="flex items-center"
                            style={{
                              padding: '8px',
                              gap: '4px',
                              background: mainBg,
                              border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                              borderRadius: '16px',
                            }}
                          >
                            <div
                              className="flex items-center flex-1"
                              style={{
                                padding: '8px 12px',
                                gap: '4px',
                                background: mainBg,
                                borderRadius: '8px',
                              }}
                            >
                              <span style={{ color: isDark ? '#62748E' : '#90A1B9', display: 'flex', flexShrink: 0 }}>
                                {getInputIcon()}
                              </span>
                              <input
                                type="text"
                                value={addFilterValue}
                                onChange={e => setAddFilterValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddItem();
                                  }
                                }}
                                placeholder={getInputPlaceholder()}
                                className="flex-1 outline-none"
                                style={{
                                  fontFamily: 'Geist, sans-serif',
                                  background: 'transparent',
                                  border: 'none',
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: addFilterValue ? titleColor : (isDark ? '#62748E' : '#90A1B9'),
                                  padding: 0,
                                  width: '100%',
                                }}
                              />
                            </div>
                            {/* Add button: 36x36 square, border-radius: 8px */}
                            <button
                              className="flex items-center justify-center transition-opacity hover:opacity-90"
                              style={{
                                width: '36px',
                                height: '36px',
                                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: addFilterValue.trim() ? 'pointer' : 'default',
                                flexShrink: 0,
                                opacity: addFilterValue.trim() ? 1 : 0.5,
                              }}
                              onClick={handleAddItem}
                              disabled={!addFilterValue.trim()}
                            >
                              <Plus size={20} color="#FFFFFF" />
                            </button>
                          </div>

                          {/* Added items tags — for non-searchable (keywords) */}
                          {addedItems.length > 0 && (
                            <div className="flex flex-wrap" style={{ gap: '8px', marginTop: '8px' }}>
                              {addedItems.map((item, idx) => {
                                const tagStyle = getTagStyle(addTarget);
                                return (
                                  <div
                                    key={idx}
                                    className="flex items-center"
                                    style={{
                                      background: tagStyle.bg,
                                      borderRadius: '8px',
                                      padding: '6px 10px',
                                      gap: '4px',
                                    }}
                                  >
                                    <span style={{
                                      fontFamily: 'Geist, sans-serif',
                                      fontSize: '14px',
                                      lineHeight: '20px',
                                      fontWeight: 500,
                                      color: tagStyle.color,
                                    }}>
                                      {item.filter}
                                    </span>
                                    <button
                                      className="flex items-center justify-center transition-opacity hover:opacity-70"
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                      onClick={() => handleRemoveAddedItem(item.filter)}
                                    >
                                      <X size={16} color={tagStyle.xColor} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected count text */}
                {addTarget && addedItems.length > 0 && (
                  <span style={{
                    fontFamily: 'Geist, sans-serif',
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontWeight: 400,
                    color: isDark ? '#62748E' : '#90A1B9',
                    textAlign: 'center',
                    width: '100%',
                  }}>
                    {getSelectedCountText()}
                  </span>
                )}

                {/* Action button */}
                {addTarget && (
                  <button
                    className="flex items-center justify-center w-full transition-opacity hover:opacity-90"
                    onClick={(e) => { e.stopPropagation(); handleSaveCriteria(); }}
                    disabled={addedItems.length === 0 || isSavingCriteria}
                    style={{
                      padding: '8px 12px',
                      height: '36px',
                      background: isDark
                        ? 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%), #51A2FF'
                        : 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%), #2B7FFF',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (addedItems.length === 0 || isSavingCriteria) ? 'default' : 'pointer',
                      opacity: (addedItems.length === 0 || isSavingCriteria) ? 0.5 : 1,
                      gap: '4px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {isSavingCriteria && (
                      <Loader2 size={20} color="#FFFFFF" className="animate-spin" />
                    )}
                    <span style={{
                      fontFamily: 'Geist, sans-serif',
                      fontSize: '14px',
                      lineHeight: '20px',
                      fontWeight: 500,
                      color: '#FFFFFF',
                    }}>
                      {isSavingCriteria
                        ? t('common.saving', 'Saving...')
                        : getActionButtonText()}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default RuleDetail;
